import React, {useEffect, useRef, useState} from 'react'
import PropTypes from "prop-types";
import {useSearchParams} from "react-router-dom";
import {PrimeIcons} from "primereact/api";
import ConfigUtil from "../../../../util/ConfigUtil";
import FetchUtil from "../../../../util/FetchUtil";
import {NAVIGATION_ITEM} from "../../../../constants/navigationItems";
import {openEventOperator, openTournament} from "../../../../containers/views/public/PublicTournamentHelper";
import LoginDialog from "../tournaments/LoginDialog";
import UmkreissucheComp from "../tournaments/UmkreissucheComp";
import LastEventComp from "../../../LastEventComp";
import ErrorInfoComponent from "../../../general/ErrorInfoComponent";
import SeitenKopf from "./SeitenKopf";
import EventListe from "./EventListe";
import VeranstalterSuche from "./VeranstalterSuche";
import AktualisierungsHinweis from "./AktualisierungsHinweis";
import useAutoAktualisierung from "./useAutoAktualisierung";
import "../tournamentneu/publicTournamentNeu.css";
import "./publicTournamentsNeu.css";

const MIN_SUCHLAENGE = 3;

const BEREICHE = [
    {id: "events", label: "Events", icon: PrimeIcons.TH_LARGE},
    {id: "live", label: "Livescoring", icon: PrimeIcons.EYE},
    {id: "veranstalter", label: "Veranstalter", icon: PrimeIcons.BUILDING},
];

/**
 * Öffentliche Eventübersicht im neuen Design. Der Bereich steht in der URL (?tab=...).
 */
function PublicTournamentsNeu({isLoggedIn}) {

    const [searchParams, setSearchParams] = useSearchParams();
    const bereich = BEREICHE.find(b => b.id === searchParams.get("tab")) || BEREICHE[0];

    const [errorMessage, setErrorMessage] = useState(undefined);
    const [showLoginDialog, setShowLoginDialog] = useState(false);
    const [tournaments, setTournaments] = useState(undefined);
    const [tournamentsLive, setTournamentsLive] = useState([]);

    const [suche, setSuche] = useState("");
    const [showSteeldarts, setShowSteeldarts] = useState(true);
    const [showEDart, setShowEDart] = useState(true);
    const [showOnline, setShowOnline] = useState(true);
    // {plz, km} oder null, wenn keine Umkreissuche aktiv ist. Gefiltert wird im Backend.
    const [umkreis, setUmkreis] = useState(null);
    const [umkreisPlzUnbekannt, setUmkreisPlzUnbekannt] = useState(false);
    // nur die Antwort zur zuletzt angefragten Umkreissuche übernehmen
    const aktuellerUmkreis = useRef(null);

    // die Live-Events werden regelmäßig neu geladen, die übrigen Events nur beim Öffnen und bei der Umkreissuche
    const liveAktualisierung = useAutoAktualisierung((onErfolg) => ladeLiveEvents(onErfolg));

    useEffect(() => {
        ladeEvents(umkreis);
    }, [umkreis]);

    const ladeLiveEvents = (onErfolg) => {
        FetchUtil.fetchGet(ConfigUtil.getConfig().resourceUrls.public + "/tournaments/live",
            json => {
                setTournamentsLive(json);
                onErfolg();
            },
            // ohne Fehlermeldung, die bisherige Liste bleibt stehen und beim nächsten Durchlauf wird es erneut versucht
            () => {},
            () => {});
    }

    const ladeEvents = (umkreisSuche) => {
        aktuellerUmkreis.current = umkreisSuche;
        let url = ConfigUtil.getConfig().resourceUrls.public + "/tournaments/v2";
        if (umkreisSuche) {
            url += "?plz=" + encodeURIComponent(umkreisSuche.plz) + "&km=" + umkreisSuche.km;
        }
        FetchUtil.fetchGet(url,
            json => {
                if (aktuellerUmkreis.current !== umkreisSuche) {
                    return;
                }
                setTournaments(json.tournaments);
                setTournamentsLive(json.liveTournaments);
                liveAktualisierung.markiereGeladen();
                setUmkreisPlzUnbekannt(json.umkreisPlzUnbekannt === true);
            },
            responseNotOk => {
                setErrorMessage("Fehler beim Laden der Events: " + responseNotOk.message);
            },
            error => {
                setErrorMessage("Fehler beim Laden der Events: " + error.message);
            });
    }

    const wechsle = (bereichId) => {
        const params = new URLSearchParams(searchParams);
        params.set("tab", bereichId);
        setSearchParams(params);
    }

    // die Suche gilt für Events und Live-Events, erst ab 3 Zeichen, vorher sind die Treffer nicht aussagekräftig
    const sucheAktiv = suche.trim().length >= MIN_SUCHLAENGE;

    const passtZurSuche = (tournament) => {
        if (!sucheAktiv) {
            return true;
        }
        const text = suche.trim().toUpperCase();
        return [tournament.bezeichnung, tournament.eventOperatorBezeichnung, tournament.leagueOperatorBezeichnung, tournament.austragungsortOrt]
            .some(wert => wert !== null && wert !== undefined && wert.toUpperCase().includes(text));
    }

    const filterEvents = () => {
        return (tournaments || []).filter(tournament => {
            if (tournament.online && !showOnline) {
                return false;
            }
            if (!tournament.online && tournament.dartType === "STEELDARTS" && !showSteeldarts) {
                return false;
            }
            if (!tournament.online && tournament.dartType === "EDART" && !showEDart) {
                return false;
            }
            return passtZurSuche(tournament);
        });
    }

    const getSuchfeld = (placeholder) => {
        return <div className="dhpt-suche dhte-suche-links">
            <i className="pi pi-search dhpt-suche-icon" aria-hidden="true"/>
            <input id="dhte-event-suche" type="search" className="dhpt-suche-feld" placeholder={placeholder}
                   aria-label="Events durchsuchen" value={suche} onChange={(e) => setSuche(e.target.value)}/>
        </div>
    }

    const getAnzahlText = (anzahlGefiltert, anzahlGesamt, bezeichnung) => {
        return <p className="dhpt-hinweis dhte-anzahl-text">
            {anzahlGefiltert === anzahlGesamt ? anzahlGesamt + " " + bezeichnung : anzahlGefiltert + " von " + anzahlGesamt + " " + bezeichnung}
            {suche.trim().length > 0 && !sucheAktiv && " · Suche ab " + MIN_SUCHLAENGE + " Zeichen"}
        </p>
    }

    const getFilterChip = (label, aktiv, setAktiv) => {
        return <button type="button" className="dhpt-sub-button" aria-pressed={aktiv} onClick={() => setAktiv(!aktiv)}>
            {aktiv && <i className="pi pi-check" aria-hidden="true"/>}
            {label}
        </button>
    }

    const getEventsBereich = () => {
        const gefiltert = filterEvents();
        return <>
            <div className="dhpt-werkzeuge">
                {getSuchfeld("Event, Veranstalter oder Ort …")}
                <div className="dhpt-subs" role="group" aria-label="Dart-Art">
                    {getFilterChip("Steeldarts", showSteeldarts, setShowSteeldarts)}
                    {getFilterChip("E-Dart", showEDart, setShowEDart)}
                    {getFilterChip("Online", showOnline, setShowOnline)}
                </div>
                <div className="dhte-umkreis" role="group" aria-label="Umkreissuche">
                    <i className="pi pi-map-marker dhte-umkreis-icon" aria-hidden="true"/>
                    <UmkreissucheComp onChange={setUmkreis} plzUnbekannt={umkreisPlzUnbekannt}/>
                </div>
            </div>
            <div className="dhpt-inhalt-eigen">
                {tournaments === undefined
                    ? <p className="dhpt-hinweis">Events werden geladen …</p>
                    : <>
                        {getAnzahlText(gefiltert.length, tournaments.length, "Events")}
                        <EventListe tournaments={gefiltert} leerText="Keine Events zu deiner Auswahl gefunden."
                                     onOpenEventOperator={(tournament) => openEventOperator(tournament)}
                                     onOpen={(tournament) => openTournament(tournament, isLoggedIn, () => setShowLoginDialog(true))}/>
                    </>
                }
            </div>
        </>
    }

    const getLiveBereich = () => {
        const gefiltert = tournamentsLive.filter(passtZurSuche);
        return <>
            <div className="dhpt-werkzeuge">
                {getSuchfeld("Live-Event, Veranstalter oder Ort …")}
            </div>
            <div className="dhpt-inhalt-eigen">
                <AktualisierungsHinweis aktualisierung={liveAktualisierung}/>
                {tournamentsLive.length > 0 && getAnzahlText(gefiltert.length, tournamentsLive.length, "Live-Events")}
                <EventListe tournaments={gefiltert} live={true} nachMonatGruppieren={false}
                            leerText={tournamentsLive.length === 0 ? "Aktuell keine Live-Events." : "Keine Live-Events zu deiner Suche gefunden."}
                            onOpenEventOperator={(tournament) => openEventOperator(tournament)}
                            onOpen={(tournament) => window.open(ConfigUtil.getBasicClientUrl() + NAVIGATION_ITEM.PUBLIC_TOURNAMENT_LIVE_VIEW.route + "?tournamentid=" + tournament.id, "_self")}/>
            </div>
        </>
    }

    const getVeranstalterBereich = () => {
        return <VeranstalterSuche/>
    }

    const meta = [];
    if (tournaments !== undefined) {
        meta.push({icon: "pi pi-calendar", text: tournaments.length + " Events"});
    }
    if (tournamentsLive.length > 0) {
        meta.push({icon: "pi pi-eye", text: tournamentsLive.length + " live"});
    }

    return <div className="dhpt-seite">
        <ErrorInfoComponent infoMessage={undefined} errorMessage={errorMessage} onClearInfoMessage={() => ""}
                            onClearErrorMessage={() => setErrorMessage(undefined)}/>
        <SeitenKopf titel="Events" meta={meta}/>
        <div className="dhpt-letzte-events">
            <LastEventComp currentId={""}/>
        </div>

        <nav className="dhpt-nav" role="tablist" aria-label="Bereiche">
            {BEREICHE.map(b => <button key={b.id} type="button" role="tab" aria-selected={b.id === bereich.id}
                                       className="dhpt-nav-button" onClick={() => wechsle(b.id)}>
                <i className={b.icon} aria-hidden="true"/>
                <span>{b.label}</span>
                {b.id === "live" && tournamentsLive.length > 0 && <span className="dhte-badge">{tournamentsLive.length}</span>}
            </button>)}
        </nav>

        <main className="dhpt-inhalt darthelfer-background-blue">
            {bereich.id === "events" && getEventsBereich()}
            {bereich.id === "live" && getLiveBereich()}
            {bereich.id === "veranstalter" && getVeranstalterBereich()}
        </main>

        {showLoginDialog &&
            <LoginDialog onClose={() => setShowLoginDialog(false)}/>
        }
    </div>
}

PublicTournamentsNeu.propTypes = {
    isLoggedIn: PropTypes.bool.isRequired,
};

export default PublicTournamentsNeu;
